import React, { FC, Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { validateEmail } from '../../utility/form_utilities';
import { classNames } from '../../utility/utility';
import { useShareListSort } from '../../hooks/api/ListSort/useShareListSort';
import { useGetSharedListSortEmails } from '../../hooks/api/ListSort/useGetSharedListSortEmails';
import { LoadingSpinner } from '../icons/OurIcons';

interface ShareListSortModalProps {
  isOpen: boolean;
  onClose: () => void;
  listSortId: string;
  listSortName: string;
}

const MAX_RECIPIENTS = 5;

const ShareListSortModal: FC<ShareListSortModalProps> = ({
  isOpen,
  onClose,
  listSortId,
  listSortName
}) => {
  const [ invalidEmail, setInvalidEmail ] = useState(false);
  const [ emails, setEmails ] = useState<string[]>([]);
  const [ emailInput, setEmailInput ] = useState('');
  const [ showDropdown, setShowDropdown ] = useState(false);
  const [ formData, setFormData ] = useState({ share_decision_data: false, share_pictures: false, share_notes: false });
  const { shareListSort, isPending } = useShareListSort();
  const { data: previousEmails } = useGetSharedListSortEmails();

  const suggestions = (previousEmails || [])
    .filter((email: string) => email.toLowerCase().includes(emailInput.toLowerCase()) && !emails.includes(email))
    .slice(0, 5);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const addEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || emails.length >= MAX_RECIPIENTS) return;

    if (!validateEmail(trimmed)) {
      setInvalidEmail(true);
      return;
    }

    if (emails.includes(trimmed)) {
      setEmailInput('');
      return;
    }

    setInvalidEmail(false);
    setEmails([ ...emails, trimmed ]);
    setEmailInput('');
    setShowDropdown(false);
  };

  const removeEmail = (value: string) => {
    setEmails(emails.filter((email) => email !== value));
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEmail(emailInput);
    } else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handleSubmit = async () => {
    let recipients = emails;

    if (emailInput.trim()) {
      if (!validateEmail(emailInput.trim())) {
        setInvalidEmail(true);
        return;
      }
      recipients = [ ...emails, emailInput.trim() ]
        .filter((email, index, arr) => arr.indexOf(email) === index)
        .slice(0, MAX_RECIPIENTS);
    }

    if (recipients.length === 0) {
      setInvalidEmail(true);
      return;
    }

    setInvalidEmail(false);
    await shareListSort({
      id: listSortId,
      emails: recipients,
      share_decision_data: formData.share_decision_data,
      share_pictures: formData.share_pictures,
      share_notes: formData.share_notes
    });
    onClose();
  };

  const ShareListSortForm: React.FC = () => {
    return (
      <>
        <div className="bg-white p-4 rounded-md" id="share-list-sort-form">
          <form>
            <div className="mb-4">
              <label className={classNames('block text-gray-600 text-start', (invalidEmail && 'text-red-500'))}>
                Share with (up to {MAX_RECIPIENTS} emails)
              </label>
              {invalidEmail && <span className='text-red-500 text-sm'>Please provide valid emails (up to {MAX_RECIPIENTS})</span>}
              <div className={classNames('w-full border rounded-md px-2 py-2 flex flex-wrap gap-2 items-center', (invalidEmail && 'border-red-500'))}>
                {emails.map((email) => (
                  <span key={email} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-sm rounded-full px-3 py-1">
                    {email}
                    <button type="button" onClick={() => removeEmail(email)} className="text-indigo-500 hover:text-indigo-900">&times;</button>
                  </span>
                ))}
                {emails.length < MAX_RECIPIENTS && (
                  <input
                    type="text"
                    value={emailInput}
                    placeholder={emails.length === 0 ? "Enter an email and press Enter" : ''}
                    onChange={(e) => { setEmailInput(e.target.value); setShowDropdown(true); }}
                    onKeyDown={handleEmailInputKeyDown}
                    onFocus={() => setShowDropdown(true)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    className="flex-1 min-w-[120px] border-none outline-none text-sm py-1"
                  />
                )}
              </div>
              {showDropdown && emailInput && suggestions.length > 0 && (
                <ul className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white shadow-sm">
                  {suggestions.map((suggestion: string) => (
                    <li
                      key={suggestion}
                      onMouseDown={() => addEmail(suggestion)}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-indigo-50 cursor-pointer"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-400 mt-1">{emails.length}/{MAX_RECIPIENTS} added</p>
            </div>
            <div className="mb-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="share_decision_data"
                  defaultChecked={formData.share_decision_data}
                  onChange={handleInputChange}
                  className="form-checkbox h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                />
                <span className="text-gray-700">Share current decision data?</span>
              </label>
            </div>
            <div className="mb-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="share_pictures"
                  defaultChecked={formData.share_pictures}
                  onChange={handleInputChange}
                  className="form-checkbox h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                />
                <span className="text-gray-700">Share pictures?</span>
              </label>
            </div>
            <div className="mb-4">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="share_notes"
                  defaultChecked={formData.share_notes}
                  onChange={handleInputChange}
                  className="form-checkbox h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                />
                <span className="text-gray-700">Share notes?</span>
              </label>
            </div>
          </form>
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
              onClick={handleSubmit}
              disabled={false}
            >
                  Share
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
              onClick={onClose}
            >
                  Cancel
            </button>
          </div>
        </div>

      </>
    );
  };

  const ShareListSortDialog: React.FC<{children: React.ReactNode}> = ({ children }) => {
    return (
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h2" className="text-xl font-semibold">
                          Share List Sort
                      </Dialog.Title>
                      <div className="text-sm text-gray-400 mb-2">
                          List to share: {listSortName}
                      </div>
                      {children}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    );
  };

  if (isPending) {
    return (
      <ShareListSortDialog>
        <LoadingSpinner />
      </ShareListSortDialog>
    );
  }

  return (
    <ShareListSortDialog>
      <ShareListSortForm />
    </ShareListSortDialog>
  );
};

export default ShareListSortModal;
